import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

interface DillSelectProps {
  value: string;
  onChange: (v: string) => void;
  options: { label: string; value: string }[];
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  /**
   * Put a filter box at the top of the list.
   *
   * Opt-in rather than automatic: on a three-option select a search box is noise, and on a list of
   * every customer a supplier has it is the only way to find one. The caller knows which it has.
   */
  searchable?: boolean;
  searchPlaceholder?: string;
}

const ChevronDown = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const SearchIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

export const DillSelect = ({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled,
  error,
  searchable = false,
  searchPlaceholder = 'Search…',
}: DillSelectProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  /** Which option Enter would take. Index into the filtered list, not the full one. */
  const [highlight, setHighlight] = useState(0);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const ref = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value);

  const visibleOptions = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!searchable || !trimmed) {
      return options;
    }
    return options.filter((option) => option.label.toLowerCase().includes(trimmed));
  }, [options, query, searchable]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideWrapper = ref.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideWrapper && !insideDropdown) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus the box as soon as the list opens, so the dropdown can be driven from the keyboard
  // without a second click. Same as AppDropDown2.
  useEffect(() => {
    if (open && searchable) {
      searchRef.current?.focus();
    }
  }, [open, searchable]);

  const handleOpen = () => {
    if (disabled) return;
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    }
    // Cleared on every open, not carried over: a stale filter would hide most of the list and give
    // no clue why. AppDropDown2 does the same on close.
    setQuery('');
    setHighlight(0);
    setOpen((p) => !p);
  };

  const handleSelect = (v: string) => {
    onChange(v);
    setQuery('');
    setOpen(false);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((current) => Math.min(current + 1, visibleOptions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter') {
      // Only when the filter has narrowed to something. Enter on an empty result would otherwise
      // pick whatever happened to be first before the filter ran.
      event.preventDefault();
      const target = visibleOptions[highlight];
      if (target) {
        handleSelect(target.value);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div ref={ref} className="relative w-full">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={handleOpen}
        className={`h-9 w-full min-w-0 flex items-center justify-between px-3 text-sm rounded-lg border transition-all outline-none
          ${
            disabled
              ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
              : error
              ? 'bg-red-50 border-red-400 text-gray-900 cursor-pointer'
              : open
              ? 'bg-white border-blue-400 ring-2 ring-blue-50 text-gray-900 cursor-pointer'
              : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400 cursor-pointer'
          }`}>
        <span
          className={`block min-w-0 truncate text-left ${
            selected ? 'text-gray-900' : 'text-gray-400'
          }`}
          title={selected ? selected.label : undefined}>
          {selected ? selected.label : placeholder}
        </span>
        <span
          className={`shrink-0 text-gray-400 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}>
          <ChevronDown />
        </span>
      </button>

      {error && <p className="mt-1 text-[11px] text-red-500 m-0">{error}</p>}

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
            {/* Outside the scrolling list, so it stays put while the options move under it. */}
            {searchable && options.length > 0 && (
              <div className="relative border-b border-gray-100">
                <span className="absolute -translate-y-1/2 left-3 top-1/2 text-gray-400">
                  <SearchIcon />
                </span>
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    // Back to the top on every keystroke: the list underneath has changed, so a
                    // held-over index would point at something the operator never looked at.
                    setHighlight(0);
                  }}
                  onKeyDown={handleSearchKeyDown}
                  placeholder={searchPlaceholder}
                  className="w-full py-2 pl-8 pr-3 text-sm bg-white border-none rounded-t-xl outline-none placeholder:text-gray-400"
                />
              </div>
            )}
            <div className="py-1 overflow-y-auto max-h-52">
              {options.length === 0 ? (
                <p className="py-4 m-0 text-xs text-center text-gray-400">No options available</p>
              ) : visibleOptions.length === 0 ? (
                <p className="py-4 m-0 text-xs text-center text-gray-400">
                  Nothing matches “{query.trim()}”
                </p>
              ) : (
                visibleOptions.map((o, index) => {
                  const active = o.value === value;
                  const focused = searchable && index === highlight;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => handleSelect(o.value)}
                      onMouseEnter={() => searchable && setHighlight(index)}
                      title={o.label}
                      className={`w-full min-w-0 flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition-colors cursor-pointer border-none
                        ${
                          active
                            ? 'bg-blue-50 text-blue-700 font-medium'
                            : focused
                            ? 'bg-gray-50 text-gray-700'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}>
                      <span className="min-w-0 truncate">{o.label}</span>
                      {active && (
                        <span className="text-blue-600 shrink-0">
                          <CheckIcon />
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
